import {ChartData} from 'chart.js';
import React from 'react';

import {PieGroup} from 'app/components/Dashboard/GenericChart/PieGroupControls';
import {BaseLensStatsWidget} from 'app/components/Dashboard/OrgLensStats/BaseLensStatsWidget';
import {FeatureMetadataApiResponse} from 'app/components/Dashboard/types';
import {makePieChartCountData} from 'app/components/Dashboard/utils/chartUtils';

/**
 * Widget that displays a pie chart of property counts by portfolio or tag
 */
const VALID_PROPERTY_COUNT_GROUPINGS: PieGroup[] = [
  {
    label: 'Portfolio',
    id: 'portfolio',
    singleMembership: true,
    groupingField: 'projectId',
    labelField: 'projectName',
  },
  {
    label: 'Property Tag',
    id: 'propertyTag',
    singleMembership: false,
    groupingField: 'tags',
    labelField: 'tags',
  },
];

export const OrgCountsWidget: React.FC<{
  featureMetadata: FeatureMetadataApiResponse[] | undefined;
  isFetching: boolean;
  hasNextPage?: boolean;
  fetchNextPage?: () => void;
  isFetchingNextPage?: boolean;
  error?: Error | null;
  percentLoaded: number | undefined;
}> = ({
  featureMetadata,
  isFetching,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
  error,
  percentLoaded,
}) => {
  const [selectedPieGroup, setSelectedPieGroup] = React.useState<PieGroup>(
    VALID_PROPERTY_COUNT_GROUPINGS[0]
  );

  const anyPropertyHasTags = featureMetadata?.some((featureMetadatum) =>
    Object.values(featureMetadatum).some((feature) => feature.tags?.length > 0)
  );

  // create chartjs pie datasets: a single pie chart is an array of datasets (usually 1) and an array of labels
  // each dataset has a data key with an array of values, one per label. plus each dataset has
  // metadata like background data, etc.
  // eg, {labels: ['Portfolio A', 'Portfolio B'], datasets: [{data: [1, 2], backgroundColor: 'red'}]}

  const chartData: ChartData<'pie'> = React.useMemo(() => {
    if (!featureMetadata) {
      return {labels: [], datasets: []} as ChartData<'pie'>;
    }

    return makePieChartCountData(featureMetadata, selectedPieGroup);
  }, [featureMetadata, selectedPieGroup]);

  // Continuously fetch data page by page so we can watch our data stream in
  // This is important for orgs with hundreds of features
  React.useEffect(() => {
    if (hasNextPage && !isFetching && !isFetchingNextPage) {
      fetchNextPage?.();
    }
  }, [hasNextPage, isFetching, isFetchingNextPage, fetchNextPage]);

  return (
    <BaseLensStatsWidget
      error={error}
      widgetConfig={{
        title: `Property Counts by ${selectedPieGroup.label}`,
        type: 'chart',
        id: -4,
        isPublished: true,
      }}
      loading={!!(isFetching || isFetchingNextPage)}
      showControls={!!anyPropertyHasTags}
      pieGroups={VALID_PROPERTY_COUNT_GROUPINGS}
      selectedPieGroup={selectedPieGroup}
      setSelectedPieGroup={setSelectedPieGroup}
      chartData={chartData}
      plugins={[]}
      countItemLabel="properties"
      showDateRangeInput={false}
      percentLoaded={percentLoaded}
    />
  );
};
